import React, { useEffect, useRef, useState } from "react";
import { ImagePlus, Trash2, Lock, Crown, Type, QrCode, Palette, Check } from "lucide-react";
import { toast } from "sonner";
import {
    getCreatorVisibility,
    updateCreatorProfile,
    uploadCreatorLogo,
    removeCreatorLogo,
} from "../lib/api";
import { tap } from "../lib/haptic";
import { track } from "../lib/track";

/**
 * CreatorStudio — Premium creator's identity beyond the Signature Backdrop.
 *
 * Four fields that AUTO-APPLY to every new card the creator makes:
 *   1. signature_line  — short sign-off text (e.g. "— George N., Brooklyn")
 *   2. qr_destination  — overrides the default scan-to-remix QR target
 *   3. brand_colors[3] — accent palette (rendering deferred to next session)
 *   4. logo            — uploaded image (rendering on card deferred too)
 *
 * Currently rendered on cards: signature_line + qr_destination.
 * Stored on user doc; copied onto card at moodcard creation. Free users
 * see locked tile with upsell. (Phase 2 will render logo + brand colors
 * into MoodCard.)
 */
const HEX_RE = /^#[0-9a-fA-F]{6}$/;

export default function CreatorStudio({ isPremium, onUpsell }) {
    const [loading, setLoading] = useState(true);
    const [signatureLine, setSignatureLine] = useState("");
    const [qrDestination, setQrDestination] = useState("");
    const [brandColors, setBrandColors] = useState(["", "", ""]);
    const [logoUrl, setLogoUrl] = useState(null);
    const [saving, setSaving] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [dirty, setDirty] = useState(false);
    const logoInputRef = useRef(null);

    useEffect(() => {
        if (!isPremium) {
            setLoading(false);
            return;
        }
        (async () => {
            try {
                const data = await getCreatorVisibility();
                setSignatureLine(data.signature_line || "");
                setQrDestination(data.qr_destination || "");
                const colors = Array.isArray(data.brand_colors) ? data.brand_colors : [];
                setBrandColors([colors[0] || "", colors[1] || "", colors[2] || ""]);
                setLogoUrl(data.logo_url || null);
            } catch { /* ignore */ }
            setLoading(false);
        })();
    }, [isPremium]);

    const save = async () => {
        const patch = {
            signature_line: signatureLine.trim() || "",
            qr_destination: qrDestination.trim() || "",
            brand_colors: brandColors.filter((c) => HEX_RE.test(c)),
        };
        // Validate QR url shape before sending — kinder error than backend 400.
        if (patch.qr_destination && !/^https?:\/\//.test(patch.qr_destination)) {
            toast("QR destination must start with http:// or https://");
            return;
        }
        setSaving(true);
        try {
            tap();
            await updateCreatorProfile(patch);
            setDirty(false);
            track("creator_studio_saved");
            toast("Saved — your style follows every new card.", { duration: 2400 });
        } catch (err) {
            const msg = err?.response?.data?.detail || "Could not save — try again.";
            toast(typeof msg === "string" ? msg : "Could not save — try again.");
        } finally {
            setSaving(false);
        }
    };

    const handleLogo = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            toast("Logo must be 5MB or smaller.");
            return;
        }
        setUploadingLogo(true);
        try {
            tap();
            const data = await uploadCreatorLogo(file);
            setLogoUrl(data.url);
            track("creator_logo_uploaded");
            toast("Logo saved.", { duration: 2000 });
        } catch (err) {
            if (err?.response?.status === 402 && onUpsell) onUpsell();
            else toast("Upload failed — try again.");
        } finally {
            setUploadingLogo(false);
        }
    };

    if (!isPremium) {
        return (
            <section className="rounded-2xl border border-app/20 bg-surface-1 p-5" data-testid="creator-studio-locked">
                <div className="flex items-center gap-2 mb-3">
                    <Type className="w-3.5 h-3.5 text-app-2" strokeWidth={1.5} />
                    <span className="font-sans text-[10px] uppercase tracking-[0.35em] text-app-3">
                        creator studio
                    </span>
                    <Lock className="w-3 h-3 text-amber-200/70 ml-auto" strokeWidth={1.6} />
                </div>
                <p className="font-serif italic text-app text-[15px] leading-snug mb-2">
                    sign every card without lifting a finger.
                </p>
                <p className="font-sans text-[11px] text-app-3 mb-4">
                    Personal sign-off · custom QR destination · brand colors · logo. Set once. Every new card carries them.
                </p>
                <button
                    type="button"
                    onClick={() => { track("creator_studio_locked_clicked"); if (onUpsell) onUpsell(); }}
                    data-testid="creator-studio-upsell-button"
                    className="btn-press inline-flex items-center gap-2 px-4 py-2 rounded-full border border-amber-300/40 bg-amber-400/10 text-amber-100 hover:bg-amber-400/20 font-sans text-[10px] uppercase tracking-[0.32em] transition-colors"
                >
                    <Crown className="w-3 h-3" strokeWidth={1.5} />
                    <span>see premium plans</span>
                </button>
            </section>
        );
    }

    return (
        <section className="rounded-2xl border border-app/20 bg-surface-1 p-5" data-testid="creator-studio-premium">
            <div className="flex items-center gap-2 mb-4">
                <Type className="w-3.5 h-3.5 text-app-2" strokeWidth={1.5} />
                <span className="font-sans text-[10px] uppercase tracking-[0.35em] text-app-3">
                    creator studio
                </span>
            </div>
            {loading ? (
                <p className="font-sans text-[11px] text-app-3">loading…</p>
            ) : (
                <div className="space-y-5">
                    {/* Signature line */}
                    <div>
                        <label className="block font-sans text-[9px] uppercase tracking-[0.32em] text-app-3 mb-2">
                            <Type className="w-3 h-3 inline mr-1.5" strokeWidth={1.5} />
                            signature line
                            <span className="text-app-3/60 normal-case tracking-normal ml-2">(up to 80 chars · renders on every card)</span>
                        </label>
                        <input
                            type="text"
                            data-testid="creator-studio-signature-input"
                            value={signatureLine}
                            maxLength={80}
                            onChange={(e) => { setSignatureLine(e.target.value); setDirty(true); }}
                            placeholder="— George N., Brooklyn"
                            className="w-full bg-app/[0.03] border border-app/20 rounded-lg px-3 py-2 font-serif italic text-sm text-app placeholder:text-app-3 outline-none focus:border-amber-300/40 transition-colors"
                        />
                    </div>

                    {/* QR destination */}
                    <div>
                        <label className="block font-sans text-[9px] uppercase tracking-[0.32em] text-app-3 mb-2">
                            <QrCode className="w-3 h-3 inline mr-1.5" strokeWidth={1.5} />
                            qr destination
                            <span className="text-app-3/60 normal-case tracking-normal ml-2">(overrides default scan-to-remix)</span>
                        </label>
                        <input
                            type="url"
                            data-testid="creator-studio-qr-input"
                            value={qrDestination}
                            onChange={(e) => { setQrDestination(e.target.value); setDirty(true); }}
                            placeholder="https://yourstudio.com"
                            className="w-full bg-app/[0.03] border border-app/20 rounded-lg px-3 py-2 font-mono text-xs text-app placeholder:text-app-3 outline-none focus:border-amber-300/40 transition-colors"
                        />
                    </div>

                    {/* Brand colors */}
                    <div>
                        <label className="block font-sans text-[9px] uppercase tracking-[0.32em] text-app-3 mb-2">
                            <Palette className="w-3 h-3 inline mr-1.5" strokeWidth={1.5} />
                            brand colors
                            <span className="text-app-3/60 normal-case tracking-normal ml-2">(3 hex · rendering coming next iteration)</span>
                        </label>
                        <div className="flex gap-2">
                            {[0, 1, 2].map((i) => (
                                <div key={i} className="flex-1 flex items-center gap-2">
                                    <span
                                        className="w-7 h-7 rounded-md border border-app/20 flex-shrink-0"
                                        style={{ background: HEX_RE.test(brandColors[i]) ? brandColors[i] : "transparent" }}
                                        aria-hidden="true"
                                    />
                                    <input
                                        type="text"
                                        data-testid={`creator-studio-color-${i}`}
                                        value={brandColors[i]}
                                        onChange={(e) => {
                                            const copy = [...brandColors];
                                            copy[i] = e.target.value;
                                            setBrandColors(copy);
                                            setDirty(true);
                                        }}
                                        placeholder="#a85a3c"
                                        className="flex-1 bg-app/[0.03] border border-app/20 rounded-md px-2 py-1.5 font-mono text-xs text-app placeholder:text-app-3 outline-none focus:border-amber-300/40 transition-colors"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Logo */}
                    <div>
                        <label className="block font-sans text-[9px] uppercase tracking-[0.32em] text-app-3 mb-2">
                            <ImagePlus className="w-3 h-3 inline mr-1.5" strokeWidth={1.5} />
                            logo
                            <span className="text-app-3/60 normal-case tracking-normal ml-2">(PNG/SVG · rendering coming next iteration)</span>
                        </label>
                        <input
                            ref={logoInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/webp,image/svg+xml"
                            className="hidden"
                            data-testid="creator-studio-logo-input"
                            onChange={handleLogo}
                        />
                        <div className="flex items-center gap-3">
                            {logoUrl && (
                                <div
                                    data-testid="creator-studio-logo-preview"
                                    className="w-12 h-12 rounded-lg border border-app/20 flex-shrink-0 bg-surface-1 flex items-center justify-center overflow-hidden"
                                >
                                    <img
                                        alt="logo"
                                        src={logoUrl.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${logoUrl}` : logoUrl}
                                        style={{ maxWidth: "100%", maxHeight: "100%" }}
                                    />
                                </div>
                            )}
                            <button
                                type="button"
                                onClick={() => logoInputRef.current?.click()}
                                disabled={uploadingLogo}
                                data-testid="creator-studio-logo-button"
                                className="btn-press flex-1 inline-flex items-center justify-center gap-2 py-2 rounded-lg border border-app/20 bg-surface-1 text-app-2 hover:text-app hover:border-app/45 font-sans text-[10px] uppercase tracking-[0.32em] transition-colors"
                            >
                                <ImagePlus className="w-3 h-3" strokeWidth={1.5} />
                                <span>{uploadingLogo ? "uploading…" : logoUrl ? "replace" : "upload"}</span>
                            </button>
                            {logoUrl && (
                                <button
                                    type="button"
                                    onClick={async () => {
                                        try { await removeCreatorLogo(); setLogoUrl(null); toast("Logo removed."); }
                                        catch { toast("Could not remove."); }
                                    }}
                                    aria-label="Remove logo"
                                    data-testid="creator-studio-logo-remove"
                                    className="btn-press flex items-center justify-center w-9 h-9 rounded-lg border border-app/20 text-app-3 hover:text-rose-400 hover:border-rose-400/40 transition-colors"
                                >
                                    <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="flex justify-end pt-2 border-t border-app/10">
                        <button
                            type="button"
                            disabled={!dirty || saving}
                            onClick={save}
                            data-testid="creator-studio-save-button"
                            className={`btn-press inline-flex items-center gap-2 px-4 py-2 rounded-full font-sans text-[10px] uppercase tracking-[0.32em] transition-colors ${
                                dirty && !saving
                                    ? "border border-amber-300/55 bg-amber-400/15 text-amber-100 hover:bg-amber-400/25"
                                    : "border border-app/15 bg-surface-1 text-app-3 opacity-65"
                            }`}
                        >
                            <Check className="w-3 h-3" strokeWidth={1.6} />
                            <span>{saving ? "saving…" : dirty ? "save" : "saved"}</span>
                        </button>
                    </div>
                </div>
            )}
        </section>
    );
}
